import ko from "knockout";
import _ from "lodash";
import { SHORT_WEEKDAY_FULL_DATETIME_OPTIONS } from "util/DateTimeHelper";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import ViewModel from "viewModels/ViewModel";

const PlaybackHeaderView = ViewModel.extend(function (
    interactionContextualTime,
    mediaType,
    isLive = false,
    isParked = false
) {
    const self = this;

    const timeFormatWithSeconds = ko.i18n.renderString(
        "interactionsList.timeFormatSecondsShort"
    );
    /**
     * display different title for different interaction types between startTime and endTime
     *
     * AudioPlayer - "playback.recording.progress": PROGRESS
     * Others      - "playback.recording.duration": DURATION
     */
    self.isLive = ko.observable(isLive);
    self.isParked = ko.observable(isParked);
    self.interactionContextualTime = ko.observable(interactionContextualTime);
    self.mediaType = ko.observable(mediaType);
    self.startTime = ko.observable("");
    self.endTime = ko.observable("");
    self.startTimeLabel = ko.observable("");
    self.endTimeLabel = ko.observable("");

    self.year = ko.observable("");
    self.day = ko.observable("");
    self.hour = ko.observable("");
    self.minute = ko.observable("");
    self.second = ko.observable("");
    self.milliSecond = ko.observable("");

    self.displayDuration = ko.observable(false);
    self.displayProgress = ko.observable(false);

    self.currentYear = ko.observable("");
    self.currentDay = ko.observable("");
    self.currentHour = ko.observable("");
    self.currentMinute = ko.observable("");
    self.currentSecond = ko.observable("");
    self.currentMilliSecond = ko.observable("");

    self.setDuration = function (elapsedTime) {
        const formattedDuration = ko.i18n.formatDurationWithUnit(elapsedTime);
        const [year, day, hour, minute, second, milliSecond] =
            formattedDuration;

        self.year(year);
        self.day(day);
        self.hour(hour);
        self.minute(minute);
        self.second(second);
        self.milliSecond(milliSecond);

        self.displayDuration(true);
    };

    self.setProgress = function (elapsedTime) {
        const formattedProgress = ko.i18n.formatDurationWithUnit(elapsedTime);
        const [year, day, hour, minute, second, milliSecond] =
            formattedProgress;

        self.currentYear(year);
        self.currentDay(day);
        self.currentHour(hour);
        self.currentMinute(minute);
        self.currentSecond(second);
        self.currentMilliSecond(milliSecond);

        self.displayProgress(true);
    };

    self.setStartEndTime = function (startDate, endDate) {
        if (startDate) {
            const formattedStartTime = ko.i18n.formatDate(
                startDate,
                timeFormatWithSeconds,
                null,
                SHORT_WEEKDAY_FULL_DATETIME_OPTIONS
            );
            self.startTime(formattedStartTime);
        }
        if (endDate) {
            const formattedEndTime = ko.i18n.formatDate(
                endDate,
                timeFormatWithSeconds,
                null,
                SHORT_WEEKDAY_FULL_DATETIME_OPTIONS
            );
            self.endTime(formattedEndTime);
        }
    };

    self.calculateDuration = function (startDate, endDate) {
        if (startDate && endDate) {
            const duration = ko.i18n.calculateDuration(startDate, endDate);
            self.setDuration(duration);
        }
    };

    self.setHeader = function (startDate, endDate) {
        self.setStartEndTime(startDate, endDate);
        self.calculateDuration(startDate, endDate);
    };

    self.computed(function () {
        if (self.isLive()) {
            self.startTimeLabel(
                ko.i18n.renderString("playback.live.startTime")
            );
            self.endTimeLabel(ko.i18n.renderString("playback.live.endTime"));
        } else {
            if (self.mediaType() === "message") {
                self.startTimeLabel(
                    ko.i18n.renderString("message.interaction.startTime")
                );
                self.endTimeLabel(
                    ko.i18n.renderString("message.interaction.endTime")
                );
            } else if (self.mediaType() === "chat") {
                self.startTimeLabel(
                    ko.i18n.renderString("chat.interaction.startTime")
                );
                self.endTimeLabel(
                    ko.i18n.renderString("chat.interaction.endTime")
                );
            } else {
                self.startTimeLabel(
                    ko.i18n.renderString("playback.recording.startTime")
                );
                self.endTimeLabel(
                    ko.i18n.renderString("playback.recording.endTime")
                );
            }
        }
    });

    self.recordingTypeIconInfo = ko.computed(function () {
        if (
            featureToggleService.isEnabled(FeatureToggle.WEM_1128_IDUI_SPARK_V4)
        ) {
            if (self.isLive && self.isLive()) {
                return { icon: "user-interactions", title: "liveInteraction" };
            } else {
                if (self.mediaType() === "call" || self.mediaType() === "audio")
                    return { icon: "phone", title: "audio" };
                if (self.mediaType() === "chat")
                    return { icon: "fa/comment-regular", title: "chat" };
                if (self.mediaType() === "screen")
                    return { icon: "screen-share-regular", title: "screen" };
                if (self.mediaType() === "voicemail")
                    return { icon: "voicemail", title: "voicemail" };
                if (self.mediaType() === "email")
                    return { icon: "message-email", title: "email" };
                if (self.mediaType() === "message")
                    return {
                        icon: "fa/message-dots-regular",
                        title: "message",
                    };
                return { icon: "phone", title: "" };
            }
        } else {
            if (self.isLive()) {
                if (self.mediaType() === "call")
                    return { icon: "new-call", title: "liveCall" };
                if (self.mediaType() === "chat")
                    return { icon: "new-chat", title: "liveChat" };
                if (self.mediaType() === "email")
                    return { icon: "new-email", title: "liveEmail" };
                if (self.mediaType() === "message")
                    return { icon: "new-message", title: "liveMessage" };
                return { icon: "new-live", title: "liveInteraction" };
            } else {
                if (self.mediaType() === "call" || self.mediaType() === "audio")
                    return { icon: "new-call", title: "audio" };
                if (self.mediaType() === "chat")
                    return { icon: "new-chat", title: "chat" };
                if (self.mediaType() === "screen")
                    return { icon: "new-screen", title: "screen" };
                if (self.mediaType() === "voicemail")
                    return { icon: "voicemail", title: "voicemail" };
                if (self.mediaType() === "email")
                    return { icon: "new-email", title: "email" };
                if (self.mediaType() === "message")
                    return { icon: "new-message", title: "message" };
                return { icon: "new-call", title: "" };
            }
        }
    });
});

export default PlaybackHeaderView;
